'use client';

import React, { useState } from 'react';
import Link from 'next/link';
import AppImage from '@/components/ui/AppImage';
import Icon from '@/components/ui/AppIcon';
import { toast } from 'sonner';

const memberData = {
  name: 'Maria Santos',
  username: 'mariasantos',
  title: 'Richésse Elite Member',
  location: 'Manila, Philippines',
  joinDate: 'March 2024',
  bio: 'Passionate about wellness and beauty, I discovered Richésse two years ago and it changed everything — my health, my confidence, and my career. I curate only the products I personally use and love.',
  avatar: "https://img.rocket.new/generatedImages/rocket_gen_img_1985c262f-1763294244026.png",
  avatarAlt: 'Smiling woman with dark hair, professional headshot with warm natural lighting',
  coverImage: "https://img.rocket.new/generatedImages/rocket_gen_img_191f10152-1764825393789.png",
  coverAlt: 'Elegant arrangement of luxury beauty and wellness products on marble surface',
  stats: [
  { id: 'stat-customers', value: '127', label: 'Happy Customers' },
  { id: 'stat-orders', value: '284', label: 'Orders Fulfilled' },
  { id: 'stat-years', value: '2.5', label: 'Years with Richésse' }],

  socials: [
  { id: 'social-ig', icon: 'CameraIcon', label: 'Instagram', href: '#' },
  { id: 'social-fb', icon: 'GlobeAltIcon', label: 'Facebook', href: '#' },
  { id: 'social-yt', icon: 'PlayCircleIcon', label: 'YouTube', href: '#' }]

};

export default function MemberHero() {
  const [copied, setCopied] = useState(false);

  const copyStoreLink = () => {
    // In production: copy window.location.href
    setCopied(true);
    toast.success('Store link copied!', {
      description: `richesse.global/${memberData.username}`
    });
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <section className="pt-24 lg:pt-28">
      {/* Cover image */}
      <div className="relative h-56 md:h-72 lg:h-80 overflow-hidden">
        <AppImage
          src={memberData.coverImage}
          alt={memberData.coverAlt}
          fill
          priority
          sizes="100vw"
          className="object-cover" />

        <div className="absolute inset-0 bg-gradient-to-b from-transparent via-transparent to-background" />
      </div>

      {/* Profile section */}
      <div className="max-w-screen-2xl mx-auto px-6 lg:px-10">
        <div className="relative -mt-16 lg:-mt-20 flex flex-col lg:flex-row lg:items-end gap-6 pb-8 border-b border-border">
          {/* Avatar */}
          <div className="relative w-28 h-28 lg:w-36 lg:h-36 rounded-2xl overflow-hidden border-4 border-background shadow-elevated flex-shrink-0">
            <AppImage
              src={memberData.avatar}
              alt={memberData.avatarAlt}
              fill
              sizes="144px"
              className="object-cover" />

          </div>

          {/* Info */}
          <div className="flex-1">
            <div className="flex flex-col sm:flex-row sm:items-start justify-between gap-4">
              <div>
                <div className="flex items-center gap-2 mb-1">
                  <h1 className="text-2xl font-bold text-foreground">{memberData.name}</h1>
                  <span className="richesse-badge bg-primary/10 text-primary text-[10px]">
                    <Icon name="CheckBadgeIcon" size={12} className="mr-1" />
                    Verified
                  </span>
                </div>
                <p className="text-sm font-medium text-primary mb-1">{memberData.title}</p>
                <div className="flex items-center gap-4 text-xs text-muted-foreground">
                  <span className="flex items-center gap-1">
                    <Icon name="MapPinIcon" size={12} />
                    {memberData.location}
                  </span>
                  <span className="flex items-center gap-1">
                    <Icon name="CalendarIcon" size={12} />
                    Member since {memberData.joinDate}
                  </span>
                </div>

                {/* Stats */}
                <div className="flex items-center gap-6 mt-3">
                  {memberData.stats.map((stat) =>
                  <div key={stat.id}>
                      <span className="text-lg font-bold text-foreground tabular-nums">{stat.value}</span>
                      <span className="text-xs text-muted-foreground ml-1.5">{stat.label}</span>
                    </div>
                  )}
                </div>
              </div>

              {/* Actions */}
              <div className="flex flex-wrap gap-2">
                {memberData.socials.map((s) =>
                <a
                  key={s.id}
                  href={s.href}
                  aria-label={s.label}
                  className="w-9 h-9 rounded-full border border-border flex items-center justify-center hover:border-primary hover:text-primary transition-colors">

                    <Icon name={s.icon as any} size={16} className="text-muted-foreground" />
                  </a>
                )}
                <button
                  onClick={copyStoreLink}
                  className="flex items-center gap-1.5 px-3 py-2 rounded-lg border border-border text-xs font-medium text-muted-foreground hover:border-primary hover:text-primary transition-colors">

                  <Icon name={copied ? 'CheckIcon' : 'LinkIcon'} size={14} />
                  {copied ? 'Copied!' : 'Share Store'}
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* Welcome message */}
        <div className="py-10 lg:py-14 grid lg:grid-cols-5 gap-8 lg:gap-16 items-center">
          <div className="lg:col-span-3">
            <span className="text-xs font-semibold tracking-widest uppercase text-primary mb-3 block">Welcome</span>
            <h2 className="font-display text-hero-lg text-foreground mb-4">
              Discover my favorite<br />Richésse products.
            </h2>
            <p className="text-muted-foreground text-base leading-relaxed mb-6 max-w-lg">
              {memberData.bio}
            </p>
            <div className="flex flex-wrap gap-3">
              <Link href="/product-catalog" className="btn-primary inline-flex items-center gap-2 px-6 py-3 rounded-xl text-sm font-semibold">
                Shop My Store
                <Icon name="ArrowRightIcon" size={16} />
              </Link>
              <a href="#about" className="btn-outline-gold inline-flex items-center gap-2 px-6 py-3 rounded-xl text-sm font-semibold">
                About Maria
              </a>
            </div>
          </div>

          {/* Quick benefits */}
          <div className="lg:col-span-2 grid grid-cols-2 gap-3">
            {[
            { id: 'benefit-auth', icon: 'ShieldCheckIcon', title: 'Authentic Products', desc: 'Direct from Richésse' },
            { id: 'benefit-ship', icon: 'TruckIcon', title: 'Fast Shipping', desc: 'Nationwide delivery' },
            { id: 'benefit-support', icon: 'ChatBubbleLeftRightIcon', title: 'Personal Support', desc: 'Talk to Maria directly' },
            { id: 'benefit-member', icon: 'StarIcon', title: 'Member Pricing', desc: 'Exclusive discounts' }].
            map((b) =>
            <div key={b.id} className="card-elevated p-4 rounded-xl">
                <Icon name={b.icon as any} size={18} className="text-primary mb-2" />
                <p className="text-xs font-semibold text-foreground">{b.title}</p>
                <p className="text-[11px] text-muted-foreground mt-0.5">{b.desc}</p>
              </div>
            )}
          </div>
        </div>
      </div>
    </section>);

}